import i18n from "i18next";
// Spec §1 — "Vocabulary, exposed not internal."
//
// API role values stay canonical (owner / editor / viewer); the UI
// renders plain-English labels. Centralizing here so every drawer
// section, error template, and topbar tooltip never disagrees.
//
// "comment" is reserved: it appears in role pickers with a "soon"
// tag (spec §1, §5) but isn't a real permission yet. Treat any
// API value of "comment" or "commenter" as the reserved tier when
// it eventually ships.

export type CanonicalRole = "owner" | "editor" | "viewer" | "comment";

// Getters: each read is in the interface's language.
export const ROLE_LABELS: Record<CanonicalRole, string> = {
  get owner() { return i18n.t("sharing:roles.owner"); },
  get editor() { return i18n.t("sharing:roles.editor"); },
  get comment() { return i18n.t("sharing:roles.comment"); },
  get viewer() { return i18n.t("sharing:roles.viewer"); },
};

export const RESERVED_ROLES: ReadonlyArray<CanonicalRole> = ["comment"];

/** Map any incoming string (capitalized, legacy, etc.) to canonical. */
export function canonicalizeRole(raw: string | null | undefined): CanonicalRole {
  const v = (raw ?? "").trim().toLowerCase();
  switch (v) {
    case "owner":
      return "owner";
    case "viewer":
    case "view":
    case "read":
      return "viewer";
    case "editor":
    case "edit":
    case "write":
    case "admin":
    case "member":
    case "":
      return "editor";
    case "comment":
    case "commenter":
      return "comment";
    default:
      return "editor";
  }
}

export function roleLabel(raw: string | null | undefined): string {
  return ROLE_LABELS[canonicalizeRole(raw)];
}

/** The selectable assignments in a role popover. Owner is excluded
 *  (always exactly one, badged not selected per spec §1 row 1). */
export const ASSIGNABLE_ROLES: ReadonlyArray<{
  value: Exclude<CanonicalRole, "owner">;
  label: string;
  reserved?: boolean;
}> = [
  { value: "editor", get label() { return ROLE_LABELS.editor; } },
  { value: "comment", get label() { return ROLE_LABELS.comment; }, reserved: true },
  { value: "viewer", get label() { return ROLE_LABELS.viewer; } },
];
